import React, {
  useMemo,
  Fragment,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as R from "ramda";
import moment from "moment";
import { Button, message, Popconfirm } from "antd";

import {
  createProject,
  projectMediaSourcesAdd,
  batchDel as batchDelApi,
  batchHidden as batchHiddenApi,
} from "../aliyun-service-api";
import { Prompt } from "./create-project";
import { downloadUrlsAsZip } from "./download-urls-as-zip";

/**
 * 通过url获取文件名
 * //i.image.cn/../../xxx.jpg?x-oss-process=image/resize,w_100
 * //i.image.cn/../../xxx.jpg
 *
 * @param url
 */
export const getFilenameByUrl = (url) => {
  return url.split("/").pop().split("?")[0];
};

export const useAddDownloadSignal = (galleryId) => {
  const [downloadedIds, setDownloadedIds] = useState(() => {
    const storedData = localStorage.getItem(`downloaded_${galleryId}`);
    return storedData ? JSON.parse(storedData) : [];
  });

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    localStorage.setItem(
      `downloaded_${galleryId}`,
      JSON.stringify(downloadedIds)
    );
    setRefreshKey(R.inc);
  }, [downloadedIds, galleryId]);

  const addDownloadTag = (newIds) => {
    setDownloadedIds((prevIds) => [...new Set([...prevIds, ...newIds])]);
  };

  const getIsDownloaded = (id) => {
    return downloadedIds.includes(id);
  };

  return { addDownloadTag, getIsDownloaded, refreshKey };
};

export const useIconType = (selectedKeys = [], list = []) => {

  const selectedKeysInList = list.filter((item) =>
    selectedKeys.includes(item)
  );

  return useMemo(() => {
    if (selectedKeysInList.length === 0) {
      return "border";
    }
    if (selectedKeysInList.length === list.length) {
      return "check-square";
    }
    return "minus-square";
  }, [selectedKeysInList, list]);
};

export const BaseBatchButtonGroup = ({
  list = [],
  selectedKeys = [],
  setSelectedKeys = R.T,
  rowKey = "id",
  children = null,
  allowClickText = false,
}) => {
  /**
   *
   * buttonGroup
   *  icon + 已选（2）+ 公开 + 隐藏 + 下载 + 合成 + 删除
   *  icon => all =>  <Icon type="check-square" />
   *  icon => part => <Icon type="minus-square" />
   *  icon => none => <Icon type="border" />
   */

  const icon = useIconType(selectedKeys, list.map(item=>item[rowKey]));

  const onClickCheckBox = useCallback(() => {
    switch (icon) {
      case "border":
        //  全选操作, 因为一个都没有选中，所以全选
        setSelectedKeys(list.map((item) => item[rowKey]));
        break;
      case "check-square":
        //  全部选中，所以清空
        setSelectedKeys([]);
        break;
      case "minus-square":
        //  部分选中，所以全选
        setSelectedKeys([]);
        break;
      default:
        break;
    }
  }, [icon, list, rowKey, setSelectedKeys]);

  return (
    <div className="flex-y-center">
      <Button.Group>
        <Button
          style={{ position: "relative", top: 1 }}
          icon={icon}
          onClick={onClickCheckBox}
          disabled={list.length === 0}
        />
        <Button
          onClick={() => {
            if (!allowClickText) {
              return;
            }
            if (list.length === 0) {
              return;
            }
            onClickCheckBox();
          }}
        >
          已选（{selectedKeys.length}）
        </Button>
        {children}
      </Button.Group>
    </div>
  );
};

export const BatchButtonGroup = ({
  orderId,
  list = [],
  selectedKeys = [],
  setSelectedKeys,
  rowKey,
  batchHidden = R.T,
  batchDel = R.T,
  addDownloadTag = R.T,
}) => {
  return (
    <BaseBatchButtonGroup
      list={list}
      selectedKeys={selectedKeys}
      setSelectedKeys={setSelectedKeys}
      rowKey={rowKey}
    >
      {selectedKeys.length > 0 && (
        <>
          <Button
            onClick={() => {
              batchHiddenApi(orderId, selectedKeys, 0).then(() => {
                message.success("公开成功");
                batchHidden(selectedKeys, 0);
              });
            }}
          >
            公开
          </Button>
          <Button
            onClick={() => {
              batchHiddenApi(orderId, selectedKeys, 1).then(() => {
                message.success("隐藏成功");
                batchHidden(selectedKeys, 1);
              });
            }}
          >
            隐藏
          </Button>
          <Button
            onClick={() => {
              const listItemInSelectedKeys = list.filter((item) =>
                selectedKeys.includes(item[rowKey])
              );

              const urls = listItemInSelectedKeys
                .map(R.path(["path"]))
                .map((path) => {
                  //  ?x-oss.*$  匹配后移除
                  return path.replace(/\?x-oss.*$/, "");
                });
              const names = listItemInSelectedKeys
                .map(R.path(["path"]))
                .map(getFilenameByUrl);
              const zipFilename = "下载.zip";
              // console.log(urls, names, zipFilename);
              try {
                (async () => {
                  await downloadUrlsAsZip(urls, names, zipFilename);
                  addDownloadTag(selectedKeys);
                  setSelectedKeys([]);
                })();
              } catch (e) {
                console.error(e);
              }
            }}
          >
            下载
          </Button>
          <Button
            style={{ display: "none" }}
            onClick={() => {
              const selectedStatusList = list
                .filter((item) => selectedKeys.includes(item[rowKey]))
                .map(R.path(["Status"]));
              const allPassEqual1 = selectedStatusList.every(R.equals(1));
              if (allPassEqual1) {
                Prompt({
                  title: "请输入合成场景名称",
                  placeholder: "请输入合成场景名称",
                  //  自动合成yyyy-MM-dd HH:mm:ss
                  initialValue:
                    "自动合成:" + moment().format("YYYY-MM-DD HH:mm:ss"),
                }).then((value) => {
                  createProject({
                    title: value,
                    orderId,
                  }).then((res) => {
                    /**
                     * {
                     *     "code": 200,
                     *     "data": {
                     *         "id": 8,
                     *         "aliyun_project_id": "2563292734d14a34a32abe79065c2ca2",
                     *         "title": "自动合成:2024-09-11 17:13:57",
                     *         "Status": 0,
                     *         "order_id": 80667,
                     *         "CreatedAt": "2024-09-11 17:14:00",
                     *         "UpdatedAt": "2024-09-11 17:14:00",
                     *         "DeletedAt": null
                     *     }
                     * }
                     */
                    const { id: projectId } = res;
                    projectMediaSourcesAdd(projectId, selectedKeys).then(() => {
                      window.open(window.location.pathname + `/${projectId}`);
                    });
                  });
                });
              } else {
                //  资源未就绪
                message.error("选择的资源有未就绪的，请重新选择");
              }
            }}
          >
            合成
          </Button>
          <Popconfirm
            title={`确认删除选中的${selectedKeys.length}项吗？`}
            onConfirm={() => {
              batchDelApi(orderId, selectedKeys).then(() => {
                message.success("删除成功");
                batchDel(selectedKeys);
              });
            }}
          >
            <Button>删除</Button>
          </Popconfirm>
        </>
      )}
    </BaseBatchButtonGroup>
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/widgets/batch-button-group.js